// Статусы платежа по контракту приходят строкой (ContractPayment.status в
// партнёрской схеме объявлен как String, не enum, см.
// billing/graphql/partner/schema.graphql): значения повторяют
// CONTRACT_PAYMENT_STATUSES бэкенда без draft, черновики партнёру не
// отдаёт резолвер запроса ни при каком фильтре.
export const CONTRACT_PAYMENT_STATUSES = [
  "pending",
  "wait_payment",
  "on_way",
  "paid",
  "paused",
  "canceled",
  "lost",
] as const;

export type ContractPaymentStatusValue =
  (typeof CONTRACT_PAYMENT_STATUSES)[number];

// Черновик в список фильтра не входит (резолвер и так отдаёт по нему пустой
// список, предлагать такой выбор партнёру незачем), но подпись для него в
// сиде есть (партнёрский фрагмент billing.status.draft, финальное ревью
// L6): раньше защитная ветка ниже отдавала в t() сырое значение "draft",
// теперь оно ушло на явную запись карты, а не на случайное совпадение имени
// статуса с суффиксом ключа.
export type ContractPaymentDisplayStatusValue =
  | ContractPaymentStatusValue
  | "draft";

// Ключи подписей (`billing.status.*`) взяты из сида
// (partnerSeed/billing.ts): статусы переводятся клиентским языком, а не
// служебными словами бэкенда.
export const CONTRACT_PAYMENT_STATUS_KEY: Record<
  ContractPaymentDisplayStatusValue,
  string
> = {
  draft: "draft",
  pending: "pending",
  wait_payment: "waitPayment",
  on_way: "onWay",
  paid: "paid",
  paused: "paused",
  canceled: "canceled",
  lost: "lost",
};

export const CONTRACT_PAYMENT_STATUS_COLOR: Record<
  ContractPaymentDisplayStatusValue,
  "default" | "info" | "success" | "warning" | "error"
> = {
  draft: "default",
  pending: "default",
  wait_payment: "default",
  on_way: "info",
  paid: "success",
  paused: "warning",
  canceled: "error",
  lost: "error",
};

function isContractPaymentDisplayStatus(
  value: string
): value is ContractPaymentDisplayStatusValue {
  return (
    value === "draft" ||
    (CONTRACT_PAYMENT_STATUSES as readonly string[]).includes(value)
  );
}

/**
 * Ключ подписи по значению статуса. Схема отдаёт status строкой, поэтому
 * значение теоретически может не совпасть со известным набором (старая
 * ссылка, рассинхрон со схемой); в этом случае подписи нет, и колонка
 * покажет сырое значение, а не упадёт с TypeError.
 */
export function contractPaymentStatusKey(status: string): string {
  return isContractPaymentDisplayStatus(status)
    ? CONTRACT_PAYMENT_STATUS_KEY[status]
    : status;
}

export function contractPaymentStatusColor(
  status: string
): "default" | "info" | "success" | "warning" | "error" {
  return isContractPaymentDisplayStatus(status)
    ? CONTRACT_PAYMENT_STATUS_COLOR[status]
    : "default";
}

// Статусы отчёта по доле с продаж, тот же приём, что у статусов счёта
// выше: строка с бэкенда (RevenueShareReport.status в партнёрской схеме
// тоже String, не enum), подписи и цвета чипа сведены в одну таблицу.
// draft у партнёра по резолверу запроса не встречается (черновик месяца
// ещё не закрыт), но подпись всё равно заведена в сиде и здесь, на
// случай прямого показа текущего месяца в будущем.
export const REVENUE_SHARE_REPORT_STATUSES = [
  "draft",
  "payable",
  "zero",
  "paid",
] as const;

export type RevenueShareReportStatusValue =
  (typeof REVENUE_SHARE_REPORT_STATUSES)[number];

// Ключи подписей `billing.reports.status.*` из сида (partnerSeed/billing.ts).
export const REVENUE_SHARE_REPORT_STATUS_KEY: Record<
  RevenueShareReportStatusValue,
  string
> = {
  draft: "draft",
  payable: "payable",
  zero: "zero",
  paid: "paid",
};

export const REVENUE_SHARE_REPORT_STATUS_COLOR: Record<
  RevenueShareReportStatusValue,
  "default" | "info" | "success" | "warning" | "error"
> = {
  draft: "default",
  payable: "info",
  zero: "default",
  paid: "success",
};

function isRevenueShareReportStatus(
  value: string
): value is RevenueShareReportStatusValue {
  return (REVENUE_SHARE_REPORT_STATUSES as readonly string[]).includes(value);
}

/**
 * Ключ подписи статуса отчёта. Как и у счёта, значение со схемы это
 * строка, а не enum: незнакомое значение показывает подпись сырым
 * текстом, а не роняет рендер TypeError'ом.
 */
export function revenueShareReportStatusKey(status: string): string {
  return isRevenueShareReportStatus(status)
    ? REVENUE_SHARE_REPORT_STATUS_KEY[status]
    : status;
}

export function revenueShareReportStatusColor(
  status: string
): "default" | "info" | "success" | "warning" | "error" {
  return isRevenueShareReportStatus(status)
    ? REVENUE_SHARE_REPORT_STATUS_COLOR[status]
    : "default";
}
